
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export const useApplicationStatus = (specificApplicationId?: string) => {
  // Get application ID from URL if present
  const getApplicationIdFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('id');
  };

  // Use the provided ID, URL ID, or null
  const applicationIdToCheck = specificApplicationId || getApplicationIdFromUrl();

  return useQuery({
    queryKey: ['applicationStatus', applicationIdToCheck],
    queryFn: async () => {
      try {
        // Check if user is authenticated
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError || !user) {
          throw new Error('User not authenticated');
        }

        // If we have a specific application ID to check, use that directly
        if (applicationIdToCheck) {
          const { data, error } = await supabase
            .from('applications')
            .select('id, status, progress')
            .eq('user_id', user.id)
            .eq('id', applicationIdToCheck)
            .single();
          
          if (!error && data) {
            console.log(`Found specific application with ID ${applicationIdToCheck}:`, data);
            return data;
          }
          
          // If not found or error, return draft state
          console.log(`Application ${applicationIdToCheck} not found or error:`, error);
          return { id: applicationIdToCheck, status: 'draft', progress: 0 };
        }

        // If no specific ID, follow original logic - first try to fetch submitted/approved/completed applications
        const { data: submittedData, error: submittedError } = await supabase
          .from('applications')
          .select('id, status, progress')
          .eq('user_id', user.id)
          .in('status', ['submitted', 'approved', 'completed'])
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        
        // If we have a submitted application, return it
        if (!submittedError && submittedData) {
          console.log('Found submitted/approved application:', submittedData);
          return submittedData;
        }
        
        // Otherwise, fall back to the most recent application (which could be a draft)
        const { data, error } = await supabase
          .from('applications')
          .select('id, status, progress')
          .eq('user_id', user.id)
          .order('updated_at', { ascending: false })
          .limit(1)
          .single();

        if (error) {
          console.error('Error fetching application status:', error);
          throw error;
        }

        return data || { id: null, status: 'draft', progress: 0 };
      } catch (error) {
        console.error('Application status error:', error);
        return { id: null, status: 'draft', progress: 0 };
      }
    },
    retry: 1,
    staleTime: 30000, // 30 seconds
  });
};
